// External Imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import AccessDenied from './AccessDenied';
import ListAllocatedVouchers from './ListAllocatedVouchers';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';

import { fetchCustomer } from '../utils/customer';

// SharePoint's ServiceStatus values are 'Active' / 'Suspended' / 'Not Active'.
function serviceStatusVariant(serviceStatus) {
  switch (serviceStatus) {
    case 'Active':
      return 'success';
    case 'Suspended':
      return 'warning';
    case 'Not Active':
      return 'danger';
    default:
      return 'secondary';
  }
}

export default function Customer() {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();
  const params = useParams();

  // Local Data
  const [customerId, setCustomerId] = useState(params.customerId);
  const [customer, setCustomer] = useState({});
  const [error, setError] = useState({ message: '', severity: 'primary', access_denied: false });
  const [loading, setLoading] = useState(true);

  const getCustomer = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    await fetchCustomer(bearerToken, customerId)
      .then((data) => {
        setCustomer(data);
        setError({ message: '', severity: 'success', access_denied: false });
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setError({ message: '', severity: 'danger', access_denied: true });
        } else {
          setError({
            message: 'Could not retrieve customer! ' + error,
            severity: 'danger',
            access_denied: false,
          });
        }
      });

    setLoading(false);
  };

  useEffect(() => {
    getCustomer();
  }, [customerId]);

  return (
    <>
      {error.message != '' ? (
        <Alert variant={error.severity}>
          <span>{error.message}</span>
        </Alert>
      ) : (
        <>
          {error.access_denied ? (
            <AccessDenied />
          ) : (
            <Row>
              <Col>
                <h1 className='text-center'>
                  [{customerId}] {customer.name}
                </h1>
              </Col>
            </Row>
          )}
        </>
      )}

      {loading ? (
        <Alert variant='warning'>
          <Spinner animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
          <span>Data loading...</span>
        </Alert>
      ) : (
        <>
          {customer.bandwidth_pools != null && customer.bandwidth_pools.length > 0 && (
            <Row className='mb-3'>
              <Col>
                <Card>
                  <Card.Header>Fibre Bandwidth Pools</Card.Header>
                  <Card.Body>
                    <Table striped>
                      <thead>
                        <tr>
                          <th>Bandwidth Pool</th>
                          <th>Description</th>
                          <th>Downlink</th>
                          <th>Uplink</th>
                          <th>Network</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customer.bandwidth_pools.map((pool) => (
                          <tr key={pool.pool_name}>
                            <th>
                              <a
                                className='btn btn-primary'
                                href={
                                  '/customer/' + customerId + '/bandwidth_pool/' + pool.pool_name
                                }
                              >
                                {pool.pool_name}
                              </a>
                            </th>
                            <td>{pool.pool_description}</td>
                            <td>{pool.downlink_mir / 1024} Mbps</td>
                            <td>{pool.uplink_mir / 1024} Mbps</td>
                            <td>{pool.uplink}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {customer.sites != null && customer.sites.length > 0 && (
            <>
              <Row className='mb-3'>
                <Col>
                  <Card>
                    <Card.Header>Fibre Sites</Card.Header>
                    <Card.Body>
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Site ID</th>
                            <th>Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customer.sites.map((site) => (
                            <tr key={site.site_id}>
                              <th>
                                <a
                                  className='btn btn-primary'
                                  href={'/customer/' + customerId + '/site/' + site.site_id}
                                >
                                  {site.site_id}
                                </a>
                              </th>
                              <td>{site.name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className='mb-3'>
                <Col>
                  <Card>
                    <Card.Header>Allocated Quota Vouchers and Reset Tokens</Card.Header>
                    <Card.Body>
                      <ListAllocatedVouchers
                        customer={customer}
                        bearerToken={getToken(auth, isValidToken, token)}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {customer.starlink_terminals != null && (
            <Row className='mb-3'>
              <Col>
                <Card>
                  <Card.Header>Starlink Terminals</Card.Header>
                  <Card.Body>
                    {customer.starlink_terminals.length === 0 ? (
                      <Alert variant='warning'>
                        No Starlink terminals were found on Starlink&apos;s API. Please contact your
                        administrator to verify the terminal IDs allocated to your account.
                      </Alert>
                    ) : (
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Terminal ID</th>
                            <th>Kit S/N</th>
                            <th>Activation Date</th>
                            <th>Location</th>
                            <th>Alias</th>
                            <th>Service Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customer.starlink_terminals.map((terminal) => (
                            <tr key={terminal.terminal_id}>
                              <td>
                                <a
                                  className='btn btn-primary'
                                  href={
                                    '/customer/' + customerId + '/starlink/' + terminal.terminal_id
                                  }
                                >
                                  {terminal.terminal_id}
                                </a>
                              </td>
                              <td>{terminal.kit_serial}</td>
                              <td>
                                {terminal.activation_date ? (
                                  new Date(terminal.activation_date).toLocaleDateString()
                                ) : (
                                  <>&nbsp;</>
                                )}
                              </td>
                              <td>{terminal.site ?? <>&nbsp;</>}</td>
                              <td>{terminal.service_line?.nickname ?? <>&nbsp;</>}</td>
                              <td>
                                {terminal.service_status ? (
                                  <Badge bg={serviceStatusVariant(terminal.service_status)}>
                                    {terminal.service_status}
                                  </Badge>
                                ) : (
                                  <>&nbsp;</>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
}
